import { useTranslation } from 'react-i18next'
import { locales, LocaleCode } from './index'

export function useLocale() {
  const { t, i18n } = useTranslation()

  const currentLocale = (i18n.language || 'en-AU') as LocaleCode
  const localeInfo = locales[currentLocale] || locales['en-AU']

  const changeLocale = (locale: LocaleCode) => {
    i18n.changeLanguage(locale)
  }

  // Helper to get nested translation values
  const getValue = <T = string>(key: string): T => {
    return t(key, { returnObjects: true }) as T
  }

  // Common locale values with type safety
  const urls = getValue<{
    apiBase: string
    sandboxBase: string
    widgetBase: string
    portalBase: string
  }>('urls')

  const currency = getValue<{
    vat: string
    code: string
    name: string
    symbol: string
    vatRate: string
    supportedCurrencies: string
  }>('currency')

  const phone = getValue<{
    format: string
    example: string
    countryCode: string
    localFormat: string
    validFormats: string
  }>('phone')

  const tax = getValue<{
    name: string
    format: string
    example: string
    authority: string
  }>('tax')

  const examples = getValue<{
    companyName: string
    personName: { given: string; surname: string; full: string }
    email: string
    emailDomain: string
    apmName: string
    apmLocation: string
    apmFindMe: string
    coordinates: { latitude: number; longitude: number }
    nationalId: { label: string; maxLength: string }
  }>('examples')

  const address = getValue<{
    postalCodeLabel: string
    postalCodeExample: string
    cityExample: string
    addressLineExample: string
    poBoxLabel: string
    poBoxExample: string
  }>('address')

  const payment = getValue<{
    methods: string[]
    primary: string
    mobileMoney: { name: string; provider: string; tillNumberLabel: string; tillNumberExample: string } | null
  }>('payment')

  const pricing = getValue<{
    small: number
    medium: number
    large: number
    codRanges: Array<{ min: number; max: number; fee: number }>
  }>('pricing')

  const support = getValue<{
    email: string
    salesEmail: string
    phone: string
    hours: string
  }>('support')

  const content = getValue<{
    overview: { networkDescription: string; coverageArea: string; billing: string }
    apmFinder: { description: string; mapTiles: string }
    billing: { invoiceCycle: string; paymentTerms: string }
  }>('content')

  const locale = getValue<{
    code: string
    name: string
    country: string
    countryCode: string
  }>('locale')

  const brand = getValue<{
    name: string
    tagline: string
    primaryColor: string
    primaryColorLight: string
    primaryColorDark: string
    activeBg: string
  }>('brand')

  return {
    t,
    i18n,
    currentLocale,
    localeInfo,
    changeLocale,
    getValue,
    // Typed values
    urls,
    currency,
    phone,
    tax,
    examples,
    address,
    payment,
    pricing,
    support,
    content,
    locale,
    brand,
  }
}

export type UseLocaleReturn = ReturnType<typeof useLocale>
